"use client";

import { useEffect, useState } from "react";
import {
  announcementImageUrl,
  getActiveAnnouncements,
  type Announcement,
} from "@/lib/api";
import type { LandingDict } from "@/content/landing";

/**
 * THE HOME-PAGE POPUP CARD (owner 2026-08-22): an announcement the platform admin posts from
 * /admin, with an image or a full flyer and a countdown, shown or hidden at will.
 *
 * What is on screen is whatever `GET /announcements/active` returns — the gateway applies the
 * visibility switch and the optional window, so this component never decides visibility and a
 * hidden announcement never reaches the browser. Dismissal is per announcement AND per edit
 * (keyed on updatedAt), so a re-post or a change shows again; it lives in localStorage, which
 * is the right scope for "I closed this" and degrades to "show it" when storage is unavailable.
 *
 * The countdown ticks every second to `countdownTo`; past it, the card says "live" rather than
 * counting negative. Layout `flyer` makes the image the card with the text overlaid at the
 * bottom; `card` keeps the image inside a text card.
 */
export function AnnouncementCard({ dict }: { dict: LandingDict["announce"] }) {
  const [ann, setAnn] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(true);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    getActiveAnnouncements()
      .then((list) => {
        if (!alive) return;
        const first = list[0] ?? null;
        setAnn(first);
        if (!first) return;
        let seen = false;
        try {
          seen = localStorage.getItem(`twoke.announce.${first.id}`) === first.updatedAt;
        } catch {
          seen = false;
        }
        setDismissed(seen);
      })
      // An unreachable gateway must never break the landing page — no card is the honest state.
      .catch(() => alive && setAnn(null));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!ann?.countdownTarget) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ann?.countdownTarget]);

  if (!ann || dismissed) return null;

  const close = () => {
    try {
      localStorage.setItem(`twoke.announce.${ann.id}`, ann.updatedAt);
    } catch {
      /* storage unavailable — the card simply returns next visit */
    }
    setDismissed(true);
  };

  const remaining = ann.countdownTarget ? Date.parse(ann.countdownTarget) - now : null;
  const parts =
    remaining !== null && remaining > 0
      ? {
          d: Math.floor(remaining / 86_400_000),
          h: Math.floor((remaining % 86_400_000) / 3_600_000),
          m: Math.floor((remaining % 3_600_000) / 60_000),
          s: Math.floor((remaining % 60_000) / 1000),
        }
      : null;
  const flyer = ann.layout === "flyer" && ann.hasImage;

  // WHERE IT SITS (owner: "right, left, top or in the center of the home page"). On phones every
  // choice collapses to the bottom edge — a corner on a 360px screen is the whole screen anyway.
  const placement: Record<typeof ann.position, string> = {
    right: "fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:p-0",
    left: "fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:inset-auto sm:bottom-6 sm:left-6 sm:p-0",
    top: "fixed inset-x-0 top-16 z-50 flex justify-center p-4 sm:top-20 sm:p-0",
    center: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4",
    // FULL (owner 2026-08-23): the flyer covers the whole home page. Responsive by construction —
    // the image is object-contain inside the viewport, so a portrait flyer on a phone and a
    // landscape one on a desktop both show entirely, never cropped, with the text overlaid.
    full: "fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-0",
  };
  const centered = ann.position === "center";
  const full = ann.position === "full";

  // THE TIMER IS THE ADMIN'S CALL (owner 2026-08-23: "the show time is a countdown ... how much
  // time before the announcement takes effect"): `showTime` decides whether visitors see the
  // ticking days/hours/minutes/seconds to `countdownTo` at all. Off = the card carries no timer,
  // and no "live" chip either — the instant is then the admin's own note, not a public clock.
  const countdown =
    ann.countdownTarget && ann.showTime && (
      <div className="mt-3 flex items-center gap-3">
        {parts ? (
          <>
            <span className={`label mb-0! ${flyer ? "text-white/75" : ""}`}>{dict.countdownLabel}</span>
            {(
              [
                [parts.d, dict.days],
                [parts.h, dict.hours],
                [parts.m, dict.minutes],
                [parts.s, dict.seconds],
              ] as const
            ).map(([v, label]) => (
              <span key={label} className="flex flex-col items-center leading-none">
                <span className="num text-2xl font-semibold text-yellow">
                  {String(v).padStart(2, "0")}
                </span>
                <span className={`text-[10px] uppercase tracking-wider ${flyer ? "text-white/75" : "text-muted"}`}>{label}</span>
              </span>
            ))}
          </>
        ) : (
          <span className="chip border-good text-good">{dict.live}</span>
        )}
      </div>
    );

  const cta =
    ann.ctaUrl && (
      <a
        href={ann.ctaUrl}
        className="btn mt-4 inline-block"
        target={ann.ctaUrl.startsWith("http") ? "_blank" : undefined}
        rel={ann.ctaUrl.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {ann.ctaLabel ?? ann.title}
      </a>
    );

  return (
    <div
      role="dialog"
      aria-modal={centered}
      aria-label={ann.title}
      className={placement[ann.position]}
      onClick={centered ? close : undefined}
    >
      <div
        className={
          full
            ? `relative h-full w-full overflow-hidden bg-black ${flyer ? "" : "overflow-y-auto p-6 sm:p-10"}`
            : `relative w-full overflow-hidden rounded-lg border border-border bg-bg-1 shadow-2xl ${
                centered ? "max-w-lg" : "max-w-md"
              } ${flyer ? "" : "max-h-[90vh] overflow-y-auto p-5"}`
        }
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label={dict.close}
          className={
            full
              ? "absolute right-3 top-3 z-20 rounded-full border-2 border-yellow bg-black px-4 py-2 text-base font-semibold text-white shadow-lg hover:bg-yellow hover:text-black sm:right-5 sm:top-5"
              : "absolute right-2 top-2 z-10 rounded px-2 py-1 text-sm text-muted hover:text-yellow"
          }
          style={flyer && !full ? { background: "rgba(0,0,0,.45)", color: "#fff" } : undefined}
        >
          {full ? `✕ ${dict.close}` : "✕"}
        </button>
        {flyer ? (
          // IMAGE AND COUNTDOWN ON SCREEN TOGETHER (owner 2026-08-23): the flyer used to stack
          // the text UNDER the image, and a tall flyer pushed the timer below the viewport — the
          // card is fixed and does not scroll. The text + countdown now OVERLAY the flyer's bottom
          // edge on a gradient, and the image itself is capped to the viewport, so whatever the
          // flyer's size, both are visible at once.
          <div className={full ? "relative h-full w-full" : "relative"}>
            {/* eslint-disable-next-line @next/next/no-img-element -- gateway-served image */}
            <img
              src={announcementImageUrl(ann)}
              alt={ann.title}
              className={full ? "block h-full w-full object-contain" : "block w-full object-contain"}
              style={full ? { background: "#000" } : { maxHeight: "78vh", background: "#000" }}
            />
            <div
              className={`absolute inset-x-0 bottom-0 text-white ${full ? "p-6 sm:p-10" : "p-5"}`}
              style={{ background: "linear-gradient(to top, rgba(0,0,0,.88) 0%, rgba(0,0,0,.6) 65%, rgba(0,0,0,0) 100%)" }}
            >
              <div className="text-lg font-semibold">{ann.title}</div>
              {ann.body && <p className="mt-1 whitespace-pre-line text-sm text-white/80">{ann.body}</p>}
              {countdown}
              {cta}
              {full && (
                <button type="button" onClick={close} className="btn-ghost mt-4 ml-3 inline-block">
                  {dict.close}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {ann.hasImage && (
              // eslint-disable-next-line @next/next/no-img-element -- gateway-served image
              <img
                src={announcementImageUrl(ann)}
                alt={ann.title}
                className="mb-4 block w-full rounded object-contain"
                style={{ maxHeight: full ? "60vh" : "45vh" }}
              />
            )}
            <div className={full ? "text-2xl font-semibold text-white sm:text-3xl" : "text-lg font-semibold"}>{ann.title}</div>
            {ann.body && <p className={`mt-2 whitespace-pre-line ${full ? "text-base text-white/80" : "text-sm text-muted"}`}>{ann.body}</p>}
            {countdown}
            {cta}
          </>
        )}
      </div>
    </div>
  );
}
